/**
 * @file   src\components\VerificationModal.tsx
 * @brief  VerificationModal component.
 * @date   JUN, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import { React, useState, useEffect, useNavigate, Link, useParams, Button, Offcanvas, Modal } from '../utils/thirdpartyComponents';
import Verifyicon from "../assets/images/verify-img.svg";
import CWInput from "src/components/CWInput";
import "../components/OffCanvas.scss";
import { pageURLs } from 'src/utils/pageUrls';
import { CommonMessages } from 'src/messages/validationMessages';
import axios from '../config/axiosConfig';
import { MessageToaster } from "src/utils/toastUtil";
import { SignupApi, LocationApi, AuthApi } from "../utils/apiUrls";
import { VerifySignupRequest, locationUpdateRequest } from '../interfaces/generalInterface';
import { formatPhoneNumber, formatPhoneNumberforapipass, setLocalStorage , getMobileOperatingSystem, getBayTypes, syncFreeSingleWashByLocation } from 'src/utils/helper';
import { IAuthRequest } from 'src/interfaces/authInterface';
import { useAppDispatch } from 'src/hooks';
import { validateUser } from 'src/store/actions/authAction';
import { buildSigninSchema } from 'src/validations/authSchema';
import { getActiveCountryCode } from 'src/utils/countryHelper';
import * as Yup from 'yup';
import { PHONE_MAX_LENGTH } from 'src/utils/constants';
import { getFirebaseToken} from "src/utils/FirebaseConfig";
import SmallLoader from "src/components/SmallLoader";
import { ServiceType } from 'src/utils/enums';
import { IBayType } from 'src/interfaces/bayinterface';

const VerificationModal = (props: any) => {
  // Route information variable
  const params = useParams();
  // Declare action dispatch.
  const dispatch = useAppDispatch();
  const [loading,setLoading] =  useState(false);

  const { show, onModalHide, verificationFrom, phoneNumber } = props;

  // Instantiate MessageToaster
  const toaster = new MessageToaster();

  // Initialize states for otp
  const [otp, setOtp] = useState(["", "", "", ""]);

  // Initialize states for resend code timer
  const [timer, setTimer] = useState<number>(30);

  // Initialize states for resend code timer
  const [showResend, setShowResend] = useState(false);

  // Initialize state to manage errors
  const [error, setError] = useState<string | null>(null)

  // Navigate object creation.
  const navigate = useNavigate();

  const [updatedNumber, setUpdatedNumber] = useState({
    custphno: '',
  })

  const [userphoneNo, setUserPhoneNo] = useState('')

  // Initialise states for errors
  const [newNumberError, setNewNumberError] = useState({
    custphno: '',
  });

  useEffect(() => {
    setUserPhoneNo(phoneNumber)
  }, [phoneNumber])

  // To autofocus first input box
  const firstInput = document.getElementById(`otpinput1`);
  useEffect(() => {
    if (firstInput) {
      (firstInput as HTMLInputElement).focus();
    }
  }, [firstInput]);

  // Reset timer and hide resend option when `show` prop changes
  useEffect(() => {
    if (show) {
      setTimer(30); // Reset timer to 30 seconds  
      setShowResend(false); // Hide the resend option
    }
  }, [show]);

  // Set up a timer interval to decrement `timer` every second
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1); // Decrease timer by 1 second
      }, 1000);
    } else {
      setShowResend(true); // Timer has expired, show resend option
    }
    return () => {
      if (interval) clearInterval(interval);  // Clear the interval
    };
  }, [timer]);

  // Triggers whenever input is entered
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {

    // Clear error
    setError(null)

    // Extract only digits from the input value
    const value = e.target.value.replace(/\D/g, '');

    // Check the length of the value to only accept one number
    if (value.length === 1) {
      if (value) {
        // Update the OTP state with the new value at the specified index
        setOtp(prevOtp => {
          const newOtp = [...prevOtp];
          newOtp[index] = value;
          return newOtp;
        });

        // Focus on the next input field if it exists
        if (index < otp.length - 1) {
          const nextInput = document.getElementById(`otpinput${index + 2}`);
          if (nextInput) {
            (nextInput as HTMLInputElement).focus();
          }
        }
      }
    }
    else if(value.length === 4){
      handleInput(e, index)
    }
    else {
      // spiting value to get new entered value
      const oldvalue = value.toString().split('');
      const newvalue = oldvalue[oldvalue.length - 1];
      if (newvalue) {
        const newnumbervalue = newvalue.replace(/\D/g, '');
        // Focus on the next input field if it exists
        if (index < otp.length - 1) {
          const nextInput = document.getElementById(`otpinput${index + 2}`);
          if (nextInput) {
            (nextInput as HTMLInputElement).focus();
          }
        }
        // check any value found in current index
        if (otp[index] === "") {
          // if current index value is empty add new value in current index
          setOtp(prevOtp => {
            const newOtp = [...prevOtp];
            newOtp[index] = newnumbervalue;
            return newOtp;
          });
        }
        else {
          // if current index value is not empty set focus to next index
          const nextInput = document.getElementById(`otpinput${index + 3}`);
          if (nextInput) {
            (nextInput as HTMLInputElement).focus();
          }
          // check the otp limit
          if (index < otp.length - 1) {
            // add value in next index
            setOtp(prevOtp => {
              const newOtp = [...prevOtp];
              newOtp[index + 1] = newnumbervalue;
              return newOtp;
            });
          }
        }

        // Focus on the next input field if it exists
        if (index < otp.length - 1) {
          const nextInput = document.getElementById(`otpinput${index + 2}`);
          if (nextInput) {
            (nextInput as HTMLInputElement).focus();
          }
        }
      }
    }
  };

  // Triggers whenever handle key
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Enter") {
      handleSubmit();
    } else if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault(); // Prevent default behavior

      // If current OTP input is empty, move focus to the previous input
      if (otp[index] === "") {
        if (index > 0) {
          const prevInput = document.getElementById(`otpinput${index}`) as HTMLInputElement;
          if (prevInput) {
            prevInput.focus();
          }
        }
      } else {
        // Clear the current OTP input if it's not empty
        setOtp(prevOtp => {
          const newOtp = [...prevOtp];
          newOtp[index] = "";
          return newOtp;
        });

        // Move focus to the current input
        const currentInput = document.getElementById(`otpinput${index}`) as HTMLInputElement;
        if (currentInput) {
          currentInput.focus();
        }
      }
    }
  };

  const handlePaste = (e) => {
    const pasteData = e.clipboardData.getData('text');
    if (pasteData.length === otp.length) {
      const newOtp = pasteData.split('');
      setOtp(newOtp);
      e.preventDefault();
      const lastInput = document.getElementById(`otpinput${4}`);
        if (lastInput) {
          (lastInput as HTMLInputElement).focus();
        }
    }
  };
  
  const handleInput = (e, index) => {
    const pasteData = e.target.value;
    // Check if the input is 4 digits (for clipboard input)
      if (pasteData.length === otp.length) {
        const newOtp = pasteData.split('');
        setOtp(newOtp);
        e.preventDefault();
        const lastInput = document.getElementById(`otpinput${4}`);
          if (lastInput) {
            (lastInput as HTMLInputElement).focus();
          }
      }
  };

  // Triggers when verification button click
  const handleSubmit = async () => {
    //join the otp values
    const enteredOtp = otp.join("");

    // Check if OTP is entered
    if (otp.includes("")) {
      setError(CommonMessages.otp_required)
    } else {
      // API call to sign up
      try {
        setLoading(true);
        const requestData: VerifySignupRequest = {
          phoneNumber: formatPhoneNumberforapipass(userphoneNo),
          otp: parseInt(enteredOtp),
          carWashId: localStorage.getItem('carWashId') ? parseInt(localStorage.getItem('carWashId')) : 0,
        };
        const { data } = await axios.post(SignupApi.VARIFY, requestData);
        if (data.error === 0) {
          setLocalStorage('CARWASH_DATA', JSON.stringify(data.data))
          const managerUserTypeId = Number(data?.data?.managerUserTypeId || 0);
         // const managerLocationId = Number(data?.data?.managerLocationId || 0);
         // const locationId = Number(data?.data?.LocationId || 0);
          const isFreeSingleWashUser =
            managerUserTypeId === 3 ||
            (managerUserTypeId == 4 );

          if (isFreeSingleWashUser) {
            localStorage.setItem('freeSinglewash', 'true');
          } else {
            localStorage.removeItem('freeSinglewash');
          }
         
          if (
            "Notification" in window && Notification?.permission === "granted"
          ) {
            handleGetFirebaseToken();
          } else {
              localStorage.removeItem('fcmToken');
            
          }
          if (verificationFrom === "signIn" && data.data.LocationId > 0) {
            navigate("/" + params.carwashname + "/" + pageURLs.home)
          } else {
            const locationData = await axios.get(LocationApi.LOCATION_FETCH);
            //If carwash have only single location we need to update that location against the user
            if (locationData.data.data.length === 1) {
              const locationId = locationData?.data?.data[0]?.locationId
              localStorage.setItem('selectedLocation', locationId)
              

              const locationUpdateData: locationUpdateRequest = {
                locationId: locationId
              };
              const locationUpdate = await axios.post(LocationApi.LOCATION_UPDATE, locationUpdateData);
              handleOnHide();
              // props.handleCloseVerificationModal()
              if (locationUpdate.data.error === 0) {
                syncFreeSingleWashByLocation(Number(locationId || 0));
                getBayTypes().then((bayTypes: IBayType[]) => {
                      if( bayTypes?.length > 1)
                      {
                        navigate("/" + params.carwashname + "/" + pageURLs.selectBayType + "?from=signup" );
                      }
                       
                        else if(bayTypes[0]?.bayTypeId == ServiceType.selfServe || bayTypes[0]?.bayTypeId == ServiceType.dogWash || bayTypes[0]?.bayTypeId == ServiceType.vacuumWash)
                        {
                          navigate("/"+params.carwashname+"/"+pageURLs.selfServeSubscriptionandwashprice+"?bayTypeId="+bayTypes[0]?.bayTypeId);
                        }
                         
                        else
                        {
                          navigate("/"+params.carwashname+"/"+pageURLs.subscriptionandwashprice);
                        }
                       
                
                    });
              
              } else {
                setOtp(["", "", "", ""]);
                if (firstInput) {
                  (firstInput as HTMLInputElement).focus();
                }
                toaster.toastError(locationUpdate.data.error);
              }
            } else {
              navigate("/" + params.carwashname + "/" + pageURLs.location_switch, { state: { origin: 'VerificationModal', frompath: 'signup' } })
            }
          }
        }
        else {
          setOtp(["", "", "", ""]);
          if (firstInput) {
            (firstInput as HTMLInputElement).focus();
          }
          // show error message
          toaster.toastError(data.message);
        }
      } catch (error: any) {
        throw new Error(error.response?.data?.message || error.message);
      }
      finally{
        setLoading(false);
      }
      // }
    }
  };
  const resendClick = (phoneno: string) => {
    const authRequest: IAuthRequest = {
      carWashId: localStorage.getItem('carWashId') ? parseInt(localStorage.getItem('carWashId')) : 0,
      phoneNumber: formatPhoneNumberforapipass(phoneno),
      CarWashURL: params.carwashname
    }
    setOtp(["", "", "", ""])
    setTimer(30); // Reset timer to 30 seconds  
    setShowResend(false); // Hide the resend option
    dispatch(validateUser(authRequest));
  };

  const handleOnHide = () => {
    setOtp(["", "", "", ""]);
    onModalHide();
  }

  const [showEditmodal, setShowEditmodal] = useState(false);

  const handleCloseEditmodal = () => setShowEditmodal(false);
  const handleShowEditmodal = () => setShowEditmodal(true);

  // Triggers whenever input is entered
  const handleNewNumber = (e: React.ChangeEvent<HTMLInputElement>) => {

    // Extract only digits from the input value
    const phone_value = e.target.value;
    if (!/^\d*$/.test(phone_value)) {
      e.target.value = phone_value.replace(/\D/g, '');
    }

    const { name, value } = e.target

    // To remove validation when user enters input

    setNewNumberError({
      ...newNumberError,
      custphno: ""
    })

    const formattedValue = formatPhoneNumber(value);
    setUpdatedNumber({
      ...updatedNumber,
      [name]: formattedValue,
    });
  }

  // On phonenumber update
  const updatePhoneNumber = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      // Validate form data
      await buildSigninSchema(getActiveCountryCode()).validate(updatedNumber, { abortEarly: false });
      //Clear errors
      setNewNumberError({
        custphno: '',
      });


      // Modify formData 
      const newData =
      {
        carWashId: localStorage.getItem('carWashId') ? parseInt(localStorage.getItem('carWashId')) : 0,
        oldPhoneNumber: formatPhoneNumberforapipass(userphoneNo),
        newPhoneNumber: formatPhoneNumberforapipass(updatedNumber.custphno)
      }

      if (newData.oldPhoneNumber === newData.newPhoneNumber) {
        setNewNumberError({
          custphno: 'Please enter a different phone number than the current one',
        });
        return false;
      }


      // API call to update number
      try {
        toaster.toastDismiss();
        const { data } = await axios.post(SignupApi.UPDATE_PHONE_NO, newData);
        if (data?.error === 0) {
          setUserPhoneNo(updatedNumber.custphno)

          // Close modal
          await handleCloseEditmodal()
          setTimer(30);


        }
        else {
          // show error message
          toaster.toastError(data?.message);
        }
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || error?.message);
      }
    } catch (error) {
      //If validation fails on submit, then errors are shown
      if (error instanceof Yup.ValidationError) {
        const newErrors: any = {};
        error.inner.forEach((err) => {
          newErrors[err.path as keyof typeof updatedNumber] = err.message;
        });
        setNewNumberError(newErrors);
      }

    }
  }

  //Get firebase token and update in DB
  const handleGetFirebaseToken = () => {
    getFirebaseToken().then((firebaseToken: any | undefined) => {
      if (firebaseToken && firebaseToken) {
        const deviceType = getMobileOperatingSystem();
        const deviceToken = firebaseToken
        localStorage.setItem('fcmToken', firebaseToken);
        updateDeviceToken(deviceType, deviceToken)
      }
    })
  };

  const updateDeviceToken = async (deviceType : number, deviceToken: string) => {
    const newformData = {
      "DeviceToken" : deviceToken,
      "DeviceType" : deviceType,
    };
    await axios.post(AuthApi.UPDATE_DEVICE_TOKEN,newformData);
  }

  return (
    <Offcanvas
      id="verify-offcanvas"
      show={show}
      onHide={() => handleOnHide()}
      {...props}
      placement="bottom"
    >
      <Offcanvas.Header closeButton></Offcanvas.Header>
      <Offcanvas.Body>
        <div className="mobile-icon text-center"><img src={Verifyicon} alt="Car Wash App" /></div>
        <h1>Verification</h1>
        <p>Enter the verification code we just sent to your phone number.</p>
        {verificationFrom != "signIn" ?
          <>
            <p className='phone'>{formatPhoneNumber(userphoneNo)}
            </p>
            <div className="note d-flex">
              <p>If you entered incorrect phone number.</p>
              <Button variant="primary" className="primary-btn btn-main change-btn" onClick={handleShowEditmodal}>Change phone number</Button>
            </div>
          </>
          : null}
        <div className="otp-wrap d-flex justify-content-center">
          {otp.map((value, index) => (
            < CWInput
              key={index}
              id={`otpinput${index + 1}`}
              autoFocus={index === 0}
              name="otpinput"
              type="text"
              inputmode="numeric" // This helps mobile devices to show a numeric keypad
              pattern="\d*" // Restrict to digits only
              errorMessage=""
              maxLength={4}
              value={value}
              onChange={(e) => handleChange(e, index)}
              onKeyPress={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
            />
          ))}
        </div>
        <div className="control-group text-center">
          <div className="timer">{`00:${timer.toString().padStart(2, '0')}`}</div>
          {!showResend ? (
            <span className="code-link">Resend Code</span>
          ) : (
            <Link to="" title="Resend Code" onClick={() => resendClick(userphoneNo)} className='active'>Resend Code</Link>
          )}
        </div>
      </Offcanvas.Body>
      <div className="offcanvas-footer">
        {error ? <span className="error-msg">{error}</span> : null}
        <Button variant="primary" className="primary-btn btn-main" onClick={handleSubmit}>Verify</Button>
      </div>
      <Modal
        show={showEditmodal}
        onHide={handleCloseEditmodal}
        centered
        className='notification-modal'>
        <form onSubmit={(e) => { updatePhoneNumber(e) }}>
          <Modal.Body>
            <CWInput
              id="custphno"
              name="custphno"
              type="text"
              inputmode="numeric" // This helps mobile devices to show a numeric keypad
              label="Change Phone Number"
              value={updatedNumber.custphno}
              onChange={handleNewNumber}
              errorMessage={newNumberError.custphno}
              maxLength={PHONE_MAX_LENGTH}
            />
          </Modal.Body>
          <Modal.Footer className='d-grid'>
            <Button variant="light" className='m-0' onClick={handleCloseEditmodal}>Cancel</Button>
            <Button variant="primary" className="primary-btn btn-main m-0" type='submit'>Save</Button>
          </Modal.Footer>
        </form>
      </Modal>
      {loading ? <SmallLoader/> : ''}
    </Offcanvas>
  );
}
export default VerificationModal;
